import { useState, useEffect } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { useApiMutation, useApi } from '../hooks/useApi'
import type { Customer } from '../types'

interface GitHubRelease {
  version: string
  createdAt: string
  hasArtifact: boolean
}

interface DeploymentConfigWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  customer: Customer
}

interface DeploymentConfig {
  version: string
  smtp2go_api_key: string
  fillout_api_key: string
  form_id: string
}

const INITIAL_CONFIG: DeploymentConfig = {
  version: '',
  smtp2go_api_key: '',
  fillout_api_key: '',
  form_id: ''
}

export function DeploymentConfigWizard({ isOpen, onClose, onSuccess, customer }: DeploymentConfigWizardProps) {
  const [config, setConfig] = useState<DeploymentConfig>(INITIAL_CONFIG)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch available versions from GitHub
  const { data: versionsData, loading: loadingVersions } = useApi<{ versions: GitHubRelease[], latest: string }>('/versions')
  const versions = versionsData?.versions || []

  const { mutate: provisionResources, loading } = useApiMutation()

  // Set default version when versions load or modal opens
  useEffect(() => {
    if (isOpen && versionsData?.latest && !config.version) {
      setConfig(prev => ({ ...prev, version: versionsData.latest }))
    }
  }, [versionsData, isOpen, config.version])

  const updateConfig = (updates: Partial<DeploymentConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }))
    // Clear errors when user starts typing
    setErrors({})
  }

  const validateConfig = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!config.version.trim()) {
      newErrors.version = 'Version is required'
    }
    if (!config.smtp2go_api_key.trim()) {
      newErrors.smtp2go_api_key = 'SMTP2GO API Key is required'
    }
    if (!config.fillout_api_key.trim()) {
      newErrors.fillout_api_key = 'Fillout API Key is required'
    }
    if (!config.form_id.trim()) {
      newErrors.form_id = 'Form ID is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateConfig()) return

    try {
      await provisionResources(`/customers/${customer.id}/provision`, {
        version: config.version,
        config: {
          FORM_ID: config.form_id,
          VITE_FORM_ID: config.form_id
        },
        secrets: {
          SMTP2GO_API_KEY: config.smtp2go_api_key,
          FILLOUT_API_KEY: config.fillout_api_key
        }
      })
      
      // Reset form and close modal
      setConfig({ ...INITIAL_CONFIG, version: versionsData?.latest || '' })
      setErrors({})
      onClose()
      onSuccess()
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to deploy infrastructure' })
    }
  }

  const handleClose = () => {
    setConfig({ ...INITIAL_CONFIG, version: versionsData?.latest || '' })
    setErrors({})
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Deploy Transport App Infrastructure" size="md">
      <div className="space-y-6">
        {/* Customer Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Deploying for:</h4>
          <div className="space-y-1">
            <p className="text-sm text-gray-700"><strong>Customer:</strong> {customer.name}</p>
            <p className="text-sm text-gray-700"><strong>Domain:</strong> {customer.domain}</p>
            <p className="text-sm text-gray-700"><strong>Region:</strong> {customer.region.toUpperCase()}</p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            🔒 <strong>Security:</strong> API keys will be stored securely in Cloudflare Secrets, never in the database.
          </p>
        </div>

        {/* Configuration Form */}
        <div className="space-y-4">
          {/* Version Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Version to Deploy *
            </label>
            <select
              value={config.version}
              onChange={(e) => updateConfig({ version: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.version ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loadingVersions}
            >
              <option value="">Select a version...</option>
              {versions.map((v) => (
                <option key={v.version} value={v.version} disabled={!v.hasArtifact}>
                  {v.version} {v.hasArtifact ? '' : '(no artifact)'}
                  {v.version === versionsData?.latest ? ' (latest)' : ''}
                </option>
              ))}
            </select>
            {errors.version && (
              <p className="mt-1 text-sm text-red-600">{errors.version}</p>
            )}
            {loadingVersions && (
              <p className="mt-1 text-sm text-gray-500">Loading versions...</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Select the GitHub Release version to deploy
            </p>
          </div>

          <Input
            label="SMTP2GO API Key *"
            value={config.smtp2go_api_key}
            type="password"
            onChange={(e) => updateConfig({ smtp2go_api_key: e.target.value })}
            error={errors.smtp2go_api_key}
            placeholder="Enter SMTP2GO API key"
            helperText="Used for sending email notifications"
          />
          
          <Input
            label="Fillout API Key *"
            value={config.fillout_api_key}
            type="password"
            onChange={(e) => updateConfig({ fillout_api_key: e.target.value })}
            error={errors.fillout_api_key}
            placeholder="Enter Fillout API key"
            helperText="Used for form integration and data collection"
          />
          
          <Input
            label="Form ID *"
            value={config.form_id}
            onChange={(e) => updateConfig({ form_id: e.target.value })}
            error={errors.form_id}
            placeholder="Enter Fillout form ID (e.g., bFYXgsu4bjeu)"
            helperText="The specific Fillout form ID for this customer"
          />
        </div>

        {/* What will be deployed */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Infrastructure to be deployed:</h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• D1 Database (with migrations)</li>
            <li>• R2 Bucket for file storage</li>
            <li>• KV Namespaces for caching and rate limiting</li>
            <li>• Worker API with all bindings and secrets</li>
            <li>• Pages frontend with environment variables</li>
            <li>• Custom domain and DNS configuration</li>
            <li>• Cron triggers for data processing</li>
          </ul>
        </div>

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} className="bg-green-600 hover:bg-green-700">
            Deploy Infrastructure
          </Button>
        </div>
      </div>
    </Modal>
  )
}